import { useCallback, useRef, useState } from "react";

import { useDndMonitor } from "@dnd-kit/core";
import {
  faBriefcase,
  faCalculatorSimple,
  faChartMixed,
  faCube,
  faFlask,
  faGear,
  faQuestionCircle,
  faRobot,
  faSearch,
  faSparkles,
  faTimer,
} from "@fortawesome/pro-regular-svg-icons";
import { Link, useOutletContext } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { useIsWorkspaceInsightsEnabled } from "src/analytics/workspace/useIsWorkspaceInsightsEnabled";
import { Flow } from "src/api/flowTypes";
import { Divider } from "src/base-components/Divider";
import { FeatureGated } from "src/base-components/FeatureGated";
import { useResizableWidth } from "src/base-components/useResizableWidth";
import { useCaseAnalyticsAccess } from "src/cases/analytics/useCaseAnalyticsAccess";
import { useCaseInsightsAccess } from "src/cases/analytics/useCaseInsightsAccess";
import { IconButton } from "src/design-system/Icon";
import { Pill } from "src/design-system/Pill";
import { Tooltip } from "src/design-system/Tooltip";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { useIsFlowDndAvailable } from "src/flowsOverview/v2/FlowDndProvider";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useEventListener } from "src/hooks/usehooks-ts";
import { useIsCaseManagementEnabled } from "src/hooks/useIsCaseManagementEnabled";
import { useIsModuleEnabled } from "src/hooks/useIsModuleEnabled";
import { useIsTaktileOrgUser } from "src/hooks/useIsTaktileOrgUser";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { AppHeaderLogo } from "src/layout/AppHeaderLogo";
import { ClickableSearchBox } from "src/layout/ClickableSearchBox";
import {
  DecisionFlowsNavItem,
  NavItem,
  NavSidebar,
  RetoolNavItem,
} from "src/layout/NavSideBar";
import { UserMenu } from "src/layout/UserMenuV2";
import { useCollapsibleMainSidebar } from "src/mainPage/mainSidebarStore";
import { useOmniboxActions } from "src/omnibox/OmniboxStore";
import { useRetoolInformation } from "src/retool/useRetoolInformation";
import {
  DashboardPageParams,
  getUrlToAgentsPage,
  getUrlToCaseAnalyticsPage,
  getUrlToCaseQueuePage,
  getUrlToCopilotPage,
  getUrlToEventsPage,
  getUrlToSettingsPage,
  getUrlToWorkspaceInsightsPage,
  getUrlToWsDashboard,
  WsDashboardPageCode,
} from "src/router/urls";
import { useCurrentPage } from "src/router/useCurrentPage";
import { canAccessSettingsPage } from "src/settings/utils";
import { useIsUnifiedCopilotEnabled } from "src/unifiedCopilot/useIsUnifiedCopilotEnabled";
import { modifierKey } from "src/utils/platformModifierKey";
import { useParamsDecode } from "src/utils/useParamsDecode";

const DndSidebarMonitor: React.FC<{
  onDragChange: (isDragging: boolean) => void;
}> = ({ onDragChange }) => {
  useDndMonitor({
    onDragStart: () => onDragChange(true),
    onDragEnd: () => onDragChange(false),
    onDragCancel: () => onDragChange(false),
  });
  return null;
};

const CaseManagementNavItem = ({
  orgId,
  wsId,
  isCurrentPage,
}: {
  orgId: string;
  wsId: string;
  isCurrentPage: boolean;
}) => {
  const { canAccess: canAccessCaseInsights } = useCaseInsightsAccess();
  const casesUrl = canAccessCaseInsights
    ? getUrlToCaseAnalyticsPage(orgId, wsId)
    : getUrlToCaseQueuePage({ orgId, wsId });

  return (
    <NavItem
      end={false}
      icon={faBriefcase}
      isActiveOverride={isCurrentPage}
      neverShowActive
      title="Cases"
      to={casesUrl}
      isCurrentPage={isCurrentPage}
    />
  );
};

export const MainSideBar: React.FC = () => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const { collapsed, setCollapsed } = useCollapsibleMainSidebar();
  const context = useOutletContext();
  const { data: eventTypes } = useEventSchemas();
  const { flow } = context as { flow: Flow | undefined };

  const { showOmnibox } = useOmniboxActions();
  const capabilities = useCapabilities();
  const { canAccess: caseAnalyticsEnabled } = useCaseAnalyticsAccess();
  const { isEnabled: workspaceInsightsEnabled } =
    useIsWorkspaceInsightsEnabled();
  const { isEnabled: isCaseManagementEnabled } = useIsCaseManagementEnabled();
  const { isEnabled: isEvalsEnabled } = useIsModuleEnabled("evals");
  const { isEnabled: isUnifiedCopilotEnabled } = useIsUnifiedCopilotEnabled();
  const isTaktileUser = useIsTaktileOrgUser();
  const { agentsEnabled } = useWorkspaceFeatureGates();
  const {
    isEntityViewPage,
    isFlowPage,
    isAuthoringPage,
    isSettingsPage,
    isCopilotPage,
    isEntitiesPage,
    isEventsPage,
    isFeaturesPage,
    isCasesSection,
    isAgentsPage,
    isAgentDetailPage,
    isWorkspaceInsightsPage,
    isEvalsPage,
  } = useCurrentPage();
  const { retoolPageUrl, retoolConnection, isCurrentPageRetool } =
    useRetoolInformation();

  // When both the native "Cases" item and the Retool "Case Management" tab are
  // shown, relabel the Retool tab so it doesn't look like a duplicate of "Cases".
  const isCasesNavItemShown =
    capabilities.cases.canAccess && isCaseManagementEnabled;

  const hasCaseNavSidebar =
    isCasesSection &&
    caseAnalyticsEnabled &&
    (capabilities.caseAnalytics.canAccess ||
      capabilities.caseTemplates.canAccess);

  const shouldBeCollapsed =
    isEntityViewPage ||
    isFlowPage ||
    isAuthoringPage ||
    isSettingsPage ||
    isAgentDetailPage ||
    hasCaseNavSidebar;

  const isDndAvailable = useIsFlowDndAvailable();
  const [isDragging, setIsDragging] = useState(false);
  const [isPointerOverSidebar, setIsPointerOverSidebar] = useState(false);

  const isDragHovering = isDragging && isPointerOverSidebar;

  const { width, isResizing, handleResizeStart } = useResizableWidth({
    side: "left",
    storageKey: "main-sidebar-width",
    minWidth: 200,
    maxWidth: 400,
    defaultWidth: 240,
  });

  // The sidebar is collapsed when:
  // - The user has collapsed the sidebar and is not dragging (a flow)
  // - The current page is a page that does not allow the sidebar to be uncollapsed
  const isCollapsed = (collapsed && !isDragHovering) || shouldBeCollapsed;

  // The browser fires a synthetic `click` on the common ancestor of mousedown
  // and mouseup after a drag. Since the resize handle is only 1px wide, that
  // ancestor is the outer sidebar div, causing the drag-end to toggle the
  // sidebar. Track resize-end so the immediately following click is ignored.
  const justFinishedResizingRef = useRef(false);
  useEventListener("mouseup", () => {
    if (!isResizing) return;
    justFinishedResizingRef.current = true;
    window.setTimeout(() => {
      justFinishedResizingRef.current = false;
    }, 0);
  });

  const toggleSidebar = useCallback(() => {
    if (justFinishedResizingRef.current) return;
    if (shouldBeCollapsed) return;
    setCollapsed?.(!collapsed);
  }, [setCollapsed, collapsed, shouldBeCollapsed]);

  const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div
      className={twJoin(
        "flex h-full flex-col",
        isResizing && "cursor-col-resize select-none",
      )}
      onClick={toggleSidebar}
      onPointerEnter={
        isDragging ? () => setIsPointerOverSidebar(true) : undefined
      }
      onPointerLeave={
        isDragging ? () => setIsPointerOverSidebar(false) : undefined
      }
    >
      {isDndAvailable && <DndSidebarMonitor onDragChange={setIsDragging} />}
      <div className="mb-5 px-3.5">
        <Link
          className="mt-4 mb-7 flex w-fit items-center gap-x-2"
          to={getUrlToWsDashboard({
            orgId,
            wsId,
            folderId: flow?.flow_folder_id,
          })}
          onClick={stopPropagation}
        >
          <AppHeaderLogo dataLoc="header-taktile-logo" />
          {!isCollapsed && (
            <p className="font-inter-semibold-13px text-gray-800">Taktile</p>
          )}
        </Link>
        {isCollapsed ? (
          <div className="py-1" onClick={stopPropagation}>
            {/* The py-1 is to account for the space added by the search box. This way the menu below does not move when the sidebar is collapsed*/}
            <Tooltip
              placement="bottom-end"
              title={
                <>
                  <span className="mr-2">Search</span>
                  <Pill fontType="text" size="sm" variant="dark-gray">
                    <Pill.Text>{modifierKey} K</Pill.Text>
                  </Pill>
                </>
              }
              asChild
            >
              <IconButton icon={faSearch} size="xs" onClick={showOmnibox} />
            </Tooltip>
          </div>
        ) : (
          <div onClick={stopPropagation}>
            <ClickableSearchBox
              dataLoc="main-sidebar-search"
              text="Search"
              onClick={showOmnibox}
            />
          </div>
        )}
      </div>
      <div className="px-2.5">
        <Divider />
      </div>
      <div className="min-h-0 flex-1">
        <NavSidebar
          collapsed={isCollapsed}
          isDragHovering={isDragHovering}
          setCollapsed={shouldBeCollapsed ? null : setCollapsed}
          width={width}
          onResizeStart={handleResizeStart}
        >
          {isUnifiedCopilotEnabled && (
            <NavItem
              dataLoc="nav-item-copilot"
              end={true}
              icon={faSparkles}
              title="Taktile Agent"
              to={getUrlToCopilotPage({ orgId, wsId })}
              isCurrentPage={isCopilotPage}
            />
          )}
          {workspaceInsightsEnabled &&
            capabilities.workspaceInsights.canAccess && (
              <NavItem
                end={false}
                icon={faChartMixed}
                title="Insights"
                to={getUrlToWorkspaceInsightsPage({ orgId, wsId })}
                isCurrentPage={isWorkspaceInsightsPage}
              />
            )}
          {capabilities.agents.canAccess && agentsEnabled && (
            <NavItem
              end={false}
              icon={faRobot}
              // Using a larger icon size for the agents icon to match the other icons in the sidebar.
              iconSize="sm"
              title="Agents"
              to={getUrlToAgentsPage({ orgId, wsId })}
              isCurrentPage={isAgentsPage}
            />
          )}
          <DecisionFlowsNavItem orgId={orgId} wsId={wsId} />

          {capabilities.entities.canAccess && (
            <FeatureGated requiredGate="entitiesEnabled">
              <NavItem
                end={false}
                icon={faCube}
                title="Entities"
                to={getUrlToWsDashboard({
                  orgId,
                  wsId,
                  page: WsDashboardPageCode.Entities,
                })}
                isCurrentPage={isEntitiesPage}
              />
            </FeatureGated>
          )}

          {capabilities.featureCatalogue.canAccess && (
            <FeatureGated requiredGate="featuresEventsEnabled">
              <NavItem
                end={false}
                icon={faTimer}
                title="Events"
                to={getUrlToEventsPage({
                  orgId,
                  wsId,
                  eventType: eventTypes?.at(0)?.event_type,
                })}
                isCurrentPage={isEventsPage}
              />
              <NavItem
                end={false}
                icon={faCalculatorSimple}
                title="Features"
                to={getUrlToWsDashboard({
                  orgId,
                  wsId,
                  page: WsDashboardPageCode.Features,
                })}
                isCurrentPage={isFeaturesPage}
              />
            </FeatureGated>
          )}
          {isTaktileUser && isEvalsEnabled && (
            <NavItem
              dataLoc="nav-item-evals"
              end={false}
              icon={faFlask}
              title="Evals"
              to={getUrlToWsDashboard({
                orgId,
                wsId,
                page: WsDashboardPageCode.Evals,
              })}
              isCurrentPage={isEvalsPage}
            />
          )}
          {isCasesNavItemShown && capabilities.cases.canAccess && (
            <CaseManagementNavItem
              orgId={orgId}
              wsId={wsId}
              isCurrentPage={isCasesSection}
            />
          )}
          {retoolPageUrl && (
            <RetoolNavItem
              dataLoc="retool-icon"
              isCurrentPageRetool={isCurrentPageRetool}
              retoolConnectionName={retoolConnection?.name ?? ""}
              retoolPageUrl={retoolPageUrl}
              label={
                isCasesNavItemShown
                  ? "Retool Case Management"
                  : "Case Management"
              }
            />
          )}

          <div className="mr-2.5">
            <Divider spacing="my-2" />
          </div>

          <NavItem
            end={false}
            icon={faQuestionCircle}
            title="Documentation"
            to="https://docs.taktile.com"
            externalLink={true}
          />

          {canAccessSettingsPage(capabilities) && (
            <NavItem
              dataLoc="header-settings-icon"
              end={false}
              icon={faGear}
              title="Settings"
              to={getUrlToSettingsPage(orgId, wsId)}
              isCurrentPage={isSettingsPage}
            />
          )}

          <li
            className="mr-2.5 flex items-center gap-x-2 rounded-lg p-1.5 hover:bg-gray-50"
            data-loc="main-sidebar-profile-content"
            onClick={stopPropagation}
          >
            <UserMenu showUsername={!isCollapsed} variant="sidebar" />
          </li>
        </NavSidebar>
      </div>
    </div>
  );
};
